/**
 * Toast Context
 * Simple toast notifications for brief confirmations
 *
 * NOTE: This provider is rendered at the app root (outside auth gate)
 * so toasts work even when logged out. The Report button only appears
 * when FeedbackContext is available.
 */

import { createContext, useContext, useState, useCallback, useEffect, useMemo, useSyncExternalStore, ReactNode } from 'react';
import { subscribeFeedbackOpener, getFeedbackOpener } from './feedbackBridge';
import './ToastContext.css';

interface ErrorContext {
  message: string;
  context?: string;
}

/** An optional clickable CTA rendered inline on the toast (#5539). */
interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: string;
  message: string;
  type?: 'success' | 'info' | 'error';
  errorContext?: ErrorContext;
  action?: ToastAction;
}

interface ToastContextValue {
  showToast: (
    message: string,
    type?: 'success' | 'info' | 'error',
    errorContext?: ErrorContext,
    action?: ToastAction,
  ) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((message: string, type: 'success' | 'info' | 'error' = 'success', errorContext?: ErrorContext, action?: ToastAction) => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, message, type, errorContext, action }]);

    // Error toasts — and any toast carrying a clickable action (#5539) — persist
    // longer (8s) so the user has time to act; plain success/info auto-dismiss
    // after 3 seconds.
    const timeout = (type === 'error' || action) ? 8000 : 3000;
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, timeout);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Surface page-edit "patch-failed" events as a toast. PageRenderer emits
  // `xavior:patch-failed` when an html-section-update event arrives with a
  // moduleId that doesn't match the currently-mounted page — historically
  // these were swallowed with a console.warn, leaving the user staring at
  // an unchanged page wondering why their edit didn't take. This listener
  // turns the silent diagnostic into a visible signal the user can act on.
  // Throttled per-event by a short dedupe window so a burst of mismatches
  // mid-stream doesn't stack toasts.
  useEffect(() => {
    let lastShownAt = 0;
    const onPatchFailed = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { reason?: string; recovered?: boolean }
        | undefined;
      // Self-healed failures (the renderer reloaded from the latest html —
      // routine during streamed builds when a section frame races the shell
      // parse) are noise here: the page is correct by the time the user
      // could read the toast. Only surface failures nothing recovered from.
      if (detail?.recovered === true) return;
      const now = Date.now();
      if (now - lastShownAt < 4000) return; // dedupe rapid-fire
      lastShownAt = now;
      const reason = detail?.reason || 'unknown';
      const message = reason === 'moduleId-mismatch'
        ? "We couldn't apply that edit to the open page — try reloading the page or asking again."
        : "An edit didn't land cleanly. If the page looks unchanged, try again or reload.";
      const id = `patch-failed-${now}`;
      setToasts(prev => [...prev, { id, message, type: 'error' }]);
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, 8000);
    };
    window.addEventListener('xavior:patch-failed', onPatchFailed as EventListener);
    return () => {
      window.removeEventListener('xavior:patch-failed', onPatchFailed as EventListener);
    };
  }, []);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-container">
        {toasts.map(toast => (
          <ToastItem
            key={toast.id}
            toast={toast}
            onDismiss={() => dismissToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

interface ToastItemProps {
  toast: Toast;
  onDismiss: () => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  // ToastProvider mounts above FeedbackProvider (toasts must work logged-out),
  // so feedback arrives via the module-scope bridge, not context. Null while
  // FeedbackProvider is unmounted — the Report button hides itself then.
  const openFeedback = useSyncExternalStore(subscribeFeedbackOpener, getFeedbackOpener);

  const handleReport = () => {
    openFeedback?.({
      message: toast.errorContext?.message || toast.message,
      stack: toast.errorContext?.context || `Toast error: ${toast.message}`,
      componentStack: 'ToastNotification',
    });
    onDismiss();
  };

  // Accessibility: `role="alert"` (urgent, screen-reader interrupts) for
  // error toasts; `role="status"` (polite, queued) for success/info. Per
  // apps/web/AGENTS.md: top-level banners/modals require a role attribute.
  // `aria-live` mirrors the role so assistive tech without `role` support
  // still gets the same treatment.
  const ariaRole = toast.type === 'error' ? 'alert' : 'status';
  const ariaLive = toast.type === 'error' ? 'assertive' : 'polite';

  return (
    <div
      className={`toast toast-${toast.type}`}
      role={ariaRole}
      aria-live={ariaLive}
    >
      {toast.type === 'success' && <span className="toast-icon">✓</span>}
      {toast.type === 'info' && <span className="toast-icon">i</span>}
      {toast.type === 'error' && <span className="toast-icon">!</span>}
      <span className="toast-message">{toast.message}</span>
      {(toast.type === 'error' || toast.action) && (
        <div className="toast-actions">
          {/* Clickable CTA (#5539) — e.g. "Open in Studio" after saving an email. */}
          {toast.action && (
            <button
              type="button"
              className="toast-action-btn"
              onClick={() => {
                toast.action?.onClick();
                onDismiss();
              }}
            >
              {toast.action.label}
            </button>
          )}
          {toast.type === 'error' && openFeedback && (
            <button
              type="button"
              className="toast-report-btn"
              onClick={handleReport}
            >
              Report
            </button>
          )}
          <button
            type="button"
            className="toast-dismiss-btn"
            onClick={onDismiss}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    // Return a no-op if not in provider (for backwards compatibility)
    return { showToast: () => {} };
  }
  return context;
}
